import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-scroll";
import { useTranslation } from "react-i18next";
import "../index.css";

// Set to false when the extraordinary regularisation window has ended to remove the banner.
export const SHOW_EXTRAORDINARY_BANNER = true;

export default function ExtraordinaryRegularisationBanner() {
    const { t } = useTranslation();
    const [dismissed, setDismissed] = useState(false);
    const bannerRef = useRef(null);

    useEffect(() => {
        if (SHOW_EXTRAORDINARY_BANNER && !dismissed) {
            document.body.classList.add('banner-visible');
        } else {
            document.body.classList.remove('banner-visible');
            document.body.classList.remove('banner-scrolled-past');
            document.documentElement.style.removeProperty('--banner-height');
        }
        return () => {
            document.body.classList.remove('banner-visible');
            document.body.classList.remove('banner-scrolled-past');
            document.documentElement.style.removeProperty('--banner-height');
        };
    }, [dismissed]);

    useEffect(() => {
        if (!SHOW_EXTRAORDINARY_BANNER || dismissed || !bannerRef.current) return;
        const setBannerHeight = () => {
            if (bannerRef.current) {
                document.documentElement.style.setProperty('--banner-height', `${bannerRef.current.offsetHeight}px`);
            }
        };
        setBannerHeight();
        const ro = new ResizeObserver(setBannerHeight);
        ro.observe(bannerRef.current);
        return () => ro.disconnect();
    }, [dismissed]);

    useEffect(() => {
        if (!SHOW_EXTRAORDINARY_BANNER || dismissed) return;
        const handleScroll = () => {
            const h = bannerRef.current?.offsetHeight ?? parseInt(document.documentElement.style.getPropertyValue('--banner-height') || '0', 10);
            if (window.scrollY >= h) {
                document.body.classList.add('banner-scrolled-past');
            } else {
                document.body.classList.remove('banner-scrolled-past');
            }
        };
        handleScroll();
        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, [dismissed]);

    if (!SHOW_EXTRAORDINARY_BANNER || dismissed) return null;

    return (
        <div ref={bannerRef} className="w-full max-w-full overflow-x-hidden opacity-0 animate-[fadeInDown_0.5s_ease-out_forwards]">
            <div className="bg-[#800020] text-white shadow-lg w-full">
                <div className="container mx-auto px-3 sm:px-4 py-3 sm:py-3.5 max-w-full">
                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2.5 sm:gap-3">
                        <div className="flex-1 min-w-0">
                            <div className="flex flex-wrap items-center gap-2 mb-1.5">
                                <h3 className="text-lg sm:text-xl font-bold">
                                    {t("banner_extraordinary_title")}
                                </h3>
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-[#9a1a3a] text-red-50">
                                    {t("banner_extraordinary_time_window")}
                                </span>
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-600 text-white">
                                    {t("banner_extraordinary_limited_time")}
                                </span>
                            </div>
                            <p className="text-sm sm:text-base text-red-100 font-medium">
                                {t("banner_extraordinary_message")}
                            </p>
                        </div>
                        <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0 w-full sm:w-auto justify-end sm:justify-start">
                            <Link
                                to="services"
                                spy={true}
                                smooth={true}
                                duration={500}
                                className="inline-flex items-center justify-center px-3 sm:px-4 py-1.5 rounded-md font-semibold bg-white text-[#800020] hover:bg-red-50 transition-colors text-xs sm:text-sm whitespace-nowrap cursor-pointer h-fit"
                            >
                                {t("banner_extraordinary_button_primary")}
                            </Link>
                            <button
                                type="button"
                                onClick={() => setDismissed(true)}
                                className="flex-shrink-0 w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center text-red-200 hover:text-white hover:bg-white/20 transition-all rounded-full cursor-pointer"
                                aria-label="Close"
                            >
                                <span className="text-base sm:text-lg leading-none font-light">&times;</span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
